// src/components/tasks/RecurrenceFields.tsx
//
// Controlled recurrence pattern editor: frequency, interval, and the
// frequency-specific extras (weekly day toggles, monthly day-of-month).
// Scheduling (start date/time, timezone) is intentionally NOT here — the parent
// form owns that, since RecurringTasksPage and TaskFormModal present it
// differently. Shared by both.

import type { RecurrenceFrequency } from '../../types/api';
import {
  FREQUENCY_CONFIG,
  FREQUENCIES,
  DAYS_OF_WEEK,
  type RecurrencePatternState,
} from '../../services/recurrence';

interface RecurrenceFieldsProps {
  value: RecurrencePatternState;
  onChange: (next: RecurrencePatternState) => void;
}

export default function RecurrenceFields({ value, onChange }: RecurrenceFieldsProps) {
  const set = (patch: Partial<RecurrencePatternState>) => onChange({ ...value, ...patch });

  const toggleDay = (day: number) =>
    set({
      daysOfWeek: value.daysOfWeek.includes(day)
        ? value.daysOfWeek.filter((d) => d !== day)
        : [...value.daysOfWeek, day].sort((a, b) => a - b),
    });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
          <select
            value={value.frequency}
            onChange={(e) => set({ frequency: e.target.value as RecurrenceFrequency })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {FREQUENCY_CONFIG[f].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Every {value.interval} {FREQUENCY_CONFIG[value.frequency].plural}
          </label>
          <input
            type="number"
            min={1}
            max={365}
            value={value.interval}
            onChange={(e) => set({ interval: parseInt(e.target.value, 10) || 1 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {value.frequency === 'weekly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Days of Week</label>
          <div className="flex gap-2 flex-wrap">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleDay(day.value)}
                aria-pressed={value.daysOfWeek.includes(day.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  value.daysOfWeek.includes(day.value)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {value.frequency === 'monthly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
          <input
            type="number"
            min={1}
            max={31}
            value={value.dayOfMonth}
            onChange={(e) => set({ dayOfMonth: parseInt(e.target.value, 10) || 1 })}
            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}
    </div>
  );
}
